import { useCallback, useMemo, useRef, useState } from "react";

import { lookbackFromRange, rangeFromLookback } from "./dateRange";
import { useUserState } from "@/hooks/userState/useUserState";

export interface PersistedDateRangeOptions {
  /** Names the range, e.g. `dateRange:faultMonitor`. Must be stable. */
  key: string;
  /** How many days back the range reaches when the user has not picked one yet. */
  defaultLookbackDays: number;
}

interface DateRange {
  fromDate: Date;
  toDate: Date;
}

/**
 * A date range that follows the user rather than the browser.
 *
 * A range ending today is stored as a rolling lookback ("last N days"), so it is still "last N
 * days" tomorrow. A range ending in the past cannot roll: it is kept for this page view only and
 * the stored lookback goes back to the default.
 */
export function usePersistedDateRange({ key, defaultLookbackDays }: PersistedDateRangeOptions) {
  const { value, setValue, isReady } = useUserState(key, { lookbackDays: defaultLookbackDays });
  // Fixed at first render: a range must not shift under the user while the page is open.
  const nowRef = useRef<Date>(undefined);
  nowRef.current ??= new Date();
  const now = nowRef.current;

  const [sessionRange, setSessionRange] = useState<DateRange | null>(null);
  const lookbackDays = value.lookbackDays;
  const rolling = useMemo(() => rangeFromLookback(lookbackDays, now), [lookbackDays, now]);
  const { fromDate, toDate } = sessionRange ?? rolling;

  // The range as of the last write, so several writes in one handler build on each other instead
  // of each on this render's values. Re-synced from state on every render.
  const latest = useRef<DateRange>({ fromDate, toDate });
  latest.current = { fromDate, toDate };

  const apply = useCallback(
    (nextFrom: Date, nextTo: Date) => {
      if (!Number.isFinite(nextFrom.getTime()) || !Number.isFinite(nextTo.getTime())) return;
      if (nextFrom.getTime() > nextTo.getTime()) return;
      latest.current = { fromDate: nextFrom, toDate: nextTo };
      const lookback = lookbackFromRange(nextFrom, nextTo, now);
      if (lookback !== null) {
        setSessionRange(null);
        setValue({ lookbackDays: lookback });
      } else {
        setSessionRange({ fromDate: nextFrom, toDate: nextTo });
        setValue({ lookbackDays: defaultLookbackDays });
      }
    },
    [defaultLookbackDays, now, setValue],
  );

  /** Writes both ends at once; a missing end keeps its latest value. */
  const setRange = useCallback(
    (nextFrom?: Date | null, nextTo?: Date | null) =>
      apply(nextFrom ?? latest.current.fromDate, nextTo ?? latest.current.toDate),
    [apply],
  );

  return { fromDate, toDate, setRange, isReady };
}
